import { LocalizedError } from '@/lib/i18n/errors'
import { getLocaleSnapshot } from '@/lib/i18n/runtime'
import { API_BASE_URL } from '@/api/api-config'

export function isPasswordRecoveryPath(path: string): boolean {
  return /^\/(?:forgot|reset)-password\/?$/.test(path)
}

export function isPasswordRecoveryRequest(url: string | undefined): boolean {
  if (!url) return false
  try {
    return /\/auth\/(?:forgot|reset)-password\/?$/.test(new URL(url, 'https://app.blazeteams.com').pathname)
  } catch {
    return false
  }
}

export function passwordProblem(password: string): string | null {
  const bytes = new TextEncoder().encode(password).length
  if (bytes < 8 || bytes > 72 || !/\p{Lu}/u.test(password) || !/\p{Ll}/u.test(password) || !/\p{Nd}/u.test(password) || !/[\p{P}\p{S}]/u.test(password)) {
    return 'password.requirementsBytes'
  }
  return null
}

export class PasswordRecoveryUnconfirmed extends LocalizedError {
  constructor(key = 'password.unconfirmed') { super('auth', key) }
}

export type PasswordRecoveryResult = { kind: 'forgot' } | { kind: 'reset'; userId: string }

export async function submitPasswordRecovery(kind: 'forgot' | 'reset', body: { email: string } | { token: string; password: string }): Promise<PasswordRecoveryResult> {
  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}/auth/${kind}-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept-Language': getLocaleSnapshot().locale },
      body: JSON.stringify(body),
      credentials: 'omit',
      cache: 'no-store',
      referrerPolicy: 'no-referrer',
      redirect: 'error',
      signal: AbortSignal.timeout(15000),
    })
  } catch {
    throw new PasswordRecoveryUnconfirmed()
  }
  if (response.status === 429) throw new LocalizedError('auth', 'errors.rateLimited', {}, response.status)
  let value: unknown
  try { value = await response.json() } catch { throw new PasswordRecoveryUnconfirmed() }
  const object = value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : undefined
  const message = object && Object.hasOwn(object, 'message') && typeof object.message === 'string' && object.message.length > 0
  const noError = object && (!Object.hasOwn(object, 'error') || object.error === null) &&
    (!Object.hasOwn(object, 'code') || object.code === 200)
  if (response.status === 200 && object && Object.hasOwn(object, 'success') && object.success === true && message && noError) {
    if (kind === 'forgot') return { kind }
    if (Object.hasOwn(object, 'user_id') && typeof object.user_id === 'string' &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(object.user_id) &&
      object.user_id !== '00000000-0000-0000-0000-000000000000') return { kind, userId: object.user_id }
    throw new PasswordRecoveryUnconfirmed()
  }
  const nativeError = object && Object.hasOwn(object, 'code') && object.code === response.status &&
    Object.hasOwn(object, 'error') && object.error === 'error' && message &&
    (!Object.hasOwn(object, 'success') || object.success === false)
  if (nativeError && kind === 'reset' && response.status === 400) {
    throw new LocalizedError('auth', 'password.expired', {}, response.status)
  }
  if (nativeError && kind === 'reset' && response.status === 422) {
    throw new LocalizedError('auth', 'password.rejected', {}, response.status)
  }
  // Never echo provider responses: they may include submitted credentials.
  throw new PasswordRecoveryUnconfirmed()
}
